<template>
  <FusionBaseDialog :open="open" :persistentOverlay="true" padded @close="close">
    <template v-slot:activator="{ props }">
      <v-btn
        v-if="showNotifications"
        v-bind="props"
        class="popover-status-icon device-notifications-icon"
        :class="showNotificationsClass"
        variant="text"
        @click.stop="open = true"
      >
        <v-badge color="accent" location="top right" offset-x="4" offset-y="0">
          <template v-slot:badge>
            <span>{{ notificationsBadgeText }}</span>
          </template>
          <v-icon color="grey" icon="mdi-bell" />
        </v-badge>
      </v-btn>
    </template>
    <template v-slot:header>
      <v-card-item>
        <template v-slot:prepend>
          <v-icon class="header-icon" icon="mdi-bell" />
        </template>
        <v-card-title class="d-flex justify-space-between">
          <div>{{ $t('map.popover.menu.deviceNotifications') }}</div>
          <v-btn variant="text" color="primary" @click="viewAll">
            {{ $t('alertHistory.viewAll') }}
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          <v-tooltip location="bottom" :max-width="600" :disabled="!isNameOverflowing">
            <template v-slot:activator="{ props }">
              <div ref="deviceName" v-bind="props" class="device-name">
                <span>{{ deviceName }}</span>
              </div>
            </template>
            <span>{{ deviceName }}</span>
          </v-tooltip>
        </v-card-subtitle>
      </v-card-item>
    </template>

    <v-list>
      <v-list-item v-for="(item, index) in notifications" :key="item.id" lines="three">
        <v-divider v-if="index > 0" style="margin-bottom: 20px" />
        <v-row no-gutters>
          <v-col>
            <v-list-item-title>{{ item.trigger.title }}</v-list-item-title>
          </v-col>
          <v-col cols="5">
            <v-list-item-title class="anchor-right" style="text-transform: capitalize">
              {{ item.trigger.severity }}
            </v-list-item-title>
          </v-col>
        </v-row>
        <v-row class="mb-0">
          <v-col>
            <v-list-item-subtitle>
              {{ item.device.name }}
            </v-list-item-subtitle>
          </v-col>
          <v-col cols="5">
            <v-list-item-subtitle class="anchor-right">
              {{ new Date(item.triggeredAt).toLocaleDateString() }}
              {{ new Date(item.triggeredAt).toLocaleTimeString() }}
            </v-list-item-subtitle>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="d-flex align-center">
            <div class="alert-message">
              <strong>{{ item.message }}</strong>
            </div>
          </v-col>
          <v-col cols="4" class="anchor-right">
            <v-btn
              class="clear-button"
              :loading="buttonLoading[item.id]"
              :disabled="clearingInProgress"
              variant="outlined"
              @click="clear(item.id)"
            >
              {{ $t('buttons.clear') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-list-item>
    </v-list>

    <template v-slot:actions>
      <v-btn
        class="clear-all-button bg-black text-white"
        :loading="clearingAllLoading"
        :disabled="clearingInProgress"
        variant="flat"
        @click="clearAll(deviceId)"
      >
        {{ $t('buttons.clearAll') }}
      </v-btn>
      <v-spacer />
      <v-btn class="bg-primary text-white" variant="flat" @click="open = false">
        {{ $t('buttons.done') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import type { Notification } from '@/types/alerts'
import { NAMESPACES } from '@/store/types'
import { ALERTS_ACTIONS, ALERTS_GETTERS } from '@/store/alerts'
import { isChildOverflowing } from '@/services/html-utilities'

export default defineComponent({
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    deviceName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: false,
      buttonLoading: {} as Record<string, boolean>,
      clearingInProgress: false,
      clearingAllLoading: false,
      isNameOverflowing: false
    }
  },
  watch: {
    notificationsCount(): void {
      if (this.notificationsCount < 1) {
        this.close()
      }
    },
    open(): void {
      if (this.open) {
        this.setIsNameOverflowing()
      }
    }
  },
  computed: {
    notifications(): Notification[] {
      return (
        this.$store.getters[
          `${NAMESPACES.ALERTS}/${ALERTS_GETTERS.GET_NOTIFICATIONS_BY_DEVICE_ID}`
        ](this.deviceId) || []
      )
    },
    notificationsCount(): number {
      const count = this.$store.getters[
        `${NAMESPACES.ALERTS}/${ALERTS_GETTERS.GET_NOTIFICATIONS_NUM_BY_DEVICE_ID}`
      ](this.deviceId)
      return typeof count === 'number' ? count : 0
    },
    notificationsBadgeText(): string {
      return this.notificationsCount > 9 ? '9+' : `${this.notificationsCount}`
    },
    showNotifications(): boolean {
      return this.notificationsCount > 0
    },
    showNotificationsClass(): string {
      return this.notificationsCount > 9 ? 'badge-wide' : ''
    }
  },
  methods: {
    close(): void {
      this.open = false
    },
    async getNotifications(): Promise<void> {
      const res = await this.$api.getAlertNotifications({
        isRead: false,
        limit: 25
      })
      if (res.ok) {
        const body = (await res.json()) as Notification[]
        this.$store.dispatch(`${NAMESPACES.ALERTS}/${ALERTS_ACTIONS.SET_NOTIFICATIONS}`, body)
      }

      this.clearingInProgress = false
      this.clearingAllLoading = false
      this.buttonLoading = {}
    },
    async clear(id: string): Promise<void> {
      this.clearingInProgress = true
      this.buttonLoading = { [id]: true }
      await this.$api.patchClearNotification(id)
      await this.getNotifications()
    },
    async clearAll(deviceId: string): Promise<void> {
      this.clearingInProgress = true
      this.clearingAllLoading = true
      await this.$api.postClearAllDeviceNotifications(deviceId)
      await this.getNotifications()
    },
    viewAll(): void {
      this.close()
      this.$router.push({ name: 'alert-history' })
    },
    async setIsNameOverflowing(): Promise<void> {
      await nextTick()
      const ref = this.$refs.deviceName as HTMLElement | HTMLElement[] | undefined
      this.isNameOverflowing = !!ref && isChildOverflowing(ref)
    }
  }
})
</script>

<style lang="scss" scoped>
.v-dialog {
  .device-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.badge-wide {
  margin-right: 6px;
}

.v-list {
  padding: 0;
  margin-left: -16px;
  margin-right: -16px;

  .anchor-right {
    text-align: end;
  }

  .alert-message {
    font-size: 0.75rem;
    opacity: 0.75;
  }
}
</style>
